import { useEffect, useRef, useState, type FormEvent } from 'react';
import styles from './CreateAssetModal.module.css';

export interface CreateAssetModalProps {
  open: boolean;
  onCancel(): void;
  /** Submitter receives the trimmed title (always non-empty — Create
   *  is disabled while trimmed length is 0). */
  onCreate(title: string): void;
}

const MAX_TITLE_LENGTH = 50;

/**
 * Modal that collects the title for a new custom asset at creation time.
 *
 * Replaces the prior "create-empty-then-edit-inline" flow. Locking the title
 * at creation kills a class of WebDAV folder-mismatch bugs caused by
 * post-upload renames (the asset's cloud folder was named after the title
 * captured on first upload, and any subsequent rename in the editable input
 * would silently desync local state from the cloud target).
 *
 * Patterns mirror DeleteAssetModal / UploadModal:
 *  - Backdrop click + Esc close (without action).
 *  - Initial focus lands on the title input.
 *  - Submit on Enter (form's onSubmit).
 *  - Create disabled until trimmed length >= 1.
 *  - Max length 50 chars (matches FB#13 cap from commit ef066f1).
 */
export function CreateAssetModal({ open, onCancel, onCreate }: CreateAssetModalProps) {
  const [title, setTitle] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state and focus the input each time the modal opens.
  useEffect(() => {
    if (open) {
      setTitle('');
      // Defer focus so the input is mounted before we grab it.
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  // Esc closes without action.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const trimmed = title.trim();
  const canCreate = trimmed.length >= 1;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canCreate) return;
    onCreate(trimmed);
  };

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-asset-modal-title"
      >
        <div className={styles.headerRow}>
          <h2 id="create-asset-modal-title" className={styles.title}>
            New custom asset
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onCancel}
            aria-label="Close"
          >
            <i className="ph-bold ph-x" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <label className={styles.fieldLabel} htmlFor="create-asset-title">
            Asset name
          </label>
          <input
            ref={inputRef}
            id="create-asset-title"
            type="text"
            className={styles.titleInput}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Spot intro v2"
            maxLength={MAX_TITLE_LENGTH}
            autoComplete="off"
          />
          <p className={styles.hint}>
            Name cannot be changed later &mdash; pick something descriptive.
          </p>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.btnGhost}
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.btnPrimary}
              disabled={!canCreate}
            >
              <i className="ph-bold ph-plus" /> Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
